import { WallLateralDatumAnchor } from "building-types/datums/datumsTypesEnum";
import { isIndexedRoofPeakDirection, RoofFlags } from "building-types/roofs/roofSystemTypes";
import type {
  RoofVolumeGenerationStageInput,
  RoofVolumeGenerationStageResult,
} from "building-types/roofVolumes/roofVolumeGenerationTypes";
import { RoofTruncationType } from "building-types/settings/roofBaseTypes";
import type { InputParamToResolveEdgeSideOptions } from "building-types/settings/roofTypes";
import { RoofSide } from "building-types/settings/roofTypes";
import { RoofPeakDirection, RoofBoundaryType } from "building-types/settings/settingsTypesEnum";
import { WallLayerType } from "building-types/walls/wallEnums";
import { assertIsNotFormula } from "doc/formula/formulaUtils";
import { roofOverhangsNew, rectangularOverhangsNew } from "doc/utils/roofTools";
import { getExteriorWallOffset } from "doc/utils/wallTypeTools";
import {
  polyLine2IsHorizontalEdge,
  polyLine2IsVerticalEdge,
  polyLine2EdgeEval,
  polyLine2ByTwoPoints,
  polyLine2EdgeCount,
  polyLine2Edge,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { getRoofPerimeterWallsOffsetsWithLayer } from "../../multigableRoofPerimeter";
import { NULL_HEEL_HEIGHT_OFFSETS } from "../../roofs/roofHeelHeightOffsets";
import { getPerimeterOverhangs } from "../../roofs/roofPerimeters";
import { isRoofEdgeAlignedToSide } from "../../roofs/roofSlopes";
import { getResolvedEndTruncationOptions, getResolvedStartTruncationOptions } from "../../roofs/roofTruncation";
import { filterEdgeByRoofSide, hasRoofFlag } from "../../roofTools/roofVolumeFlagsAndEdges";

/**
 * Generates the basic parameters of a multi-gable roof, including the roof settings,
 * heel height offsets, base height, and the overhang values for the different sides.
 */
export function generateRoofVolumeBasicParameters({
  roofVolumeOptions,
  roofVolume,
  index,
}: RoofVolumeGenerationStageInput): RoofVolumeGenerationStageResult {
  const { roofVersion } = roofVolume;
  const {
    startEdge,
    endEdge,
    inputPerimeters,
    roofInputSettings,
    mirror,
    baseHeight,
    ceilingHeight = baseHeight,
    heelHeightOffsets = NULL_HEEL_HEIGHT_OFFSETS,
    roofData,
    neighborRoofs,
    levelWallMasses,
    levelPlanarGraph,
    wallPlanes = false,
  } = roofVolumeOptions;
  const { boundingPerimeter, exactPerimeter, limitPerimeter } = inputPerimeters;
  const { wallSettings, roofSettings, roofPlanSettings, roofElevationPlanSettings } = roofInputSettings;

  const {
    peakDirection = RoofPeakDirection.AUTO,
    eaveOverhang: baseEaveOverhang,
    boundaryType = RoofBoundaryType.BOUNDING_BOX,
    overhangSideOffset = 0,
    overhangRearOffset = 0,
    overhangDatum = WallLateralDatumAnchor.OUTSIDE_FACE_OF_FRAMING,
  } = roofSettings;

  const legacyRoof = !roofSettings.useNewRoofTrimGeneration;

  const {
    roofSideOverrides,
    roofEdgeOverrides,
    roofElevationPlanSettings: subRoofElevationPlanSettings,
    roofPlanSettings: subRoofPlanSettings,
  } = roofData;

  const { rakeOverhang: baseRakeOverhang } = roofSettings;
  const legacyRoofSlope = hasRoofFlag(roofData, RoofFlags.LEGACY_SLOPE);
  const legacyHeelHeightOffsets = hasRoofFlag(roofData, RoofFlags.LEGACY_HEEL_HEIGHT_OFFSETS);
  const legacyEaveReturns = hasRoofFlag(roofData, RoofFlags.LEGACY_EAVE_RETURNS);

  const inputToResolveEdgeSideOptions: InputParamToResolveEdgeSideOptions = {
    exactPerimeter,
    boundingPerimeter,
    startEdge,
    roofSettings,
    roofSideOverrides,
    roofEdgeOverrides,
    legacyRoofSlope,
    legacyHeelHeightOffsets,
    heelHeightOffsets,
  };

  const startTruncation = getResolvedStartTruncationOptions(inputToResolveEdgeSideOptions);
  const endTruncation = getResolvedEndTruncationOptions(inputToResolveEdgeSideOptions);

  const exteriorWallOffsetFromOverhangDatum = getExteriorWallOffset(wallSettings, overhangDatum);

  const isHorizontalRoof = polyLine2IsHorizontalEdge(boundingPerimeter, startEdge + 1);
  const isVerticalRoof = polyLine2IsVerticalEdge(boundingPerimeter, startEdge + 1);

  let sideOverhangOffset = 0;

  assertIsNotFormula(overhangSideOffset);

  const startRoofOverhangOffset = isHorizontalRoof ? overhangSideOffset : 0;
  const endRoofOverhangOffset = isHorizontalRoof ? overhangSideOffset : 0;

  assertIsNotFormula(baseEaveOverhang);
  assertIsNotFormula(baseRakeOverhang);

  let baseLeftEaveOverhang = baseEaveOverhang;
  let baseRightEaveOverhang = baseEaveOverhang;

  const baseMidRoofOverhang = baseRakeOverhang;

  // fully truncated ends are treated as eaves
  let baseStartRoofOverhang =
    (startTruncation.type === RoofTruncationType.HIP ? baseEaveOverhang : baseRakeOverhang) + startRoofOverhangOffset;

  let baseEndRoofOverhang =
    (endTruncation.type === RoofTruncationType.HIP ? baseEaveOverhang : baseRakeOverhang) + endRoofOverhangOffset;

  // apply rear overhang offset for vertical roofs
  const hasIndexedPeakDirection = isIndexedRoofPeakDirection[peakDirection];
  if (isVerticalRoof) {
    if (mirror && hasIndexedPeakDirection) {
      baseStartRoofOverhang += overhangRearOffset;
    } else {
      baseEndRoofOverhang += overhangRearOffset;
    }

    // for avoding regressions
    if (roofVersion >= 1) {
      sideOverhangOffset += overhangSideOffset;
    }
  }

  // apply rear overhang offset for horizontal roofs
  if (isHorizontalRoof) {
    // for avoding regressions
    if (roofVersion >= 2) {
      if (mirror && !hasIndexedPeakDirection) {
        baseRightEaveOverhang += overhangRearOffset;
      } else {
        baseLeftEaveOverhang += overhangRearOffset;
      }
    }
  }

  // apply side overhang offset
  baseLeftEaveOverhang += sideOverhangOffset;
  baseRightEaveOverhang += sideOverhangOffset;

  let startRoofOverhang = baseStartRoofOverhang + exteriorWallOffsetFromOverhangDatum;
  let endRoofOverhang = baseEndRoofOverhang + exteriorWallOffsetFromOverhangDatum;
  let leftEaveOverhang = baseLeftEaveOverhang + exteriorWallOffsetFromOverhangDatum;
  let rightEaveOverhang = baseRightEaveOverhang + exteriorWallOffsetFromOverhangDatum;
  const midRoofOverhang = baseMidRoofOverhang + exteriorWallOffsetFromOverhangDatum;

  const updateBaseOverhangs = (roofSide: RoofSide, overhang: number) => {
    switch (roofSide) {
      case RoofSide.LEFT:
        baseLeftEaveOverhang = Math.max(overhang, baseLeftEaveOverhang);
        leftEaveOverhang = baseLeftEaveOverhang + exteriorWallOffsetFromOverhangDatum;
        break;
      case RoofSide.RIGHT:
        baseRightEaveOverhang = Math.max(overhang, baseRightEaveOverhang);
        rightEaveOverhang = baseRightEaveOverhang + exteriorWallOffsetFromOverhangDatum;
        break;
      case RoofSide.START:
        baseStartRoofOverhang = Math.max(overhang, baseStartRoofOverhang);
        startRoofOverhang = baseStartRoofOverhang + exteriorWallOffsetFromOverhangDatum;
        break;
      case RoofSide.END:
        baseEndRoofOverhang = Math.max(overhang, baseEndRoofOverhang);
        endRoofOverhang = baseEndRoofOverhang + exteriorWallOffsetFromOverhangDatum;
        break;
      default:
        // do nothing
        break;
    }
  };

  // build base overhangs
  const [midStartPointX, midStartPointY] = polyLine2EdgeEval(boundingPerimeter, startEdge, 0.5, true);
  const [midEndPointX, midEndPointY] = polyLine2EdgeEval(boundingPerimeter, endEdge, 0.5, true);
  const midRail = polyLine2ByTwoPoints(midStartPointX, midStartPointY, midEndPointX, midEndPointY);

  const baseEdgeOverhangs = getPerimeterOverhangs({
    perimeter: exactPerimeter,
    midRail,
    leftOverhang: baseLeftEaveOverhang,
    rightOverhang: baseRightEaveOverhang,
    startOverhang: baseStartRoofOverhang,
    endOverhang: baseEndRoofOverhang,
    midOverhang: baseMidRoofOverhang,
  });

  const limitEdgeOverhangs = getPerimeterOverhangs({
    perimeter: limitPerimeter,
    midRail,
    leftOverhang: leftEaveOverhang,
    rightOverhang: rightEaveOverhang,
    startOverhang: startRoofOverhang,
    endOverhang: endRoofOverhang,
    midOverhang: midRoofOverhang,
  });

  const limitEdgeNoOverhangs = getPerimeterOverhangs({
    perimeter: limitPerimeter,
    midRail,
    leftOverhang: exteriorWallOffsetFromOverhangDatum,
    rightOverhang: exteriorWallOffsetFromOverhangDatum,
    startOverhang: exteriorWallOffsetFromOverhangDatum,
    endOverhang: exteriorWallOffsetFromOverhangDatum,
    midOverhang: exteriorWallOffsetFromOverhangDatum,
  });

  // side overrides
  if (roofSideOverrides) {
    for (const roofSide in roofSideOverrides) {
      const edgeOverrides = roofSideOverrides[roofSide];

      // check valid edges for this building side
      const edgeCount = polyLine2EdgeCount(exactPerimeter);
      for (let i = 0; i < edgeCount; i++) {
        const edge = polyLine2Edge(exactPerimeter, i);
        if (!filterEdgeByRoofSide(edge, midRail, roofSide as RoofSide)) {
          continue;
        }

        if (edgeOverrides?.overhang === undefined || edgeOverrides?.overhang === null) {
          continue;
        }

        baseEdgeOverhangs[i] = edgeOverrides.overhang;

        if (boundaryType === RoofBoundaryType.CLIP_TO_PERIMETER) {
          const inverseI = edgeCount - 1 - i;
          limitEdgeOverhangs[inverseI] = edgeOverrides.overhang + exteriorWallOffsetFromOverhangDatum;
        }

        updateBaseOverhangs(roofSide as RoofSide, edgeOverrides.overhang);
      }
    }
  }

  // edge overrides
  if (roofEdgeOverrides) {
    const edgeCount = polyLine2EdgeCount(exactPerimeter);
    for (const edgeIndex in roofEdgeOverrides) {
      const edgeOverrides = roofEdgeOverrides[edgeIndex];
      const i = parseInt(edgeIndex);
      if (i >= edgeCount) {
        continue;
      }

      if (edgeOverrides?.overhang === undefined || edgeOverrides?.overhang === null) {
        continue;
      }

      const inverseI = edgeCount - 1 - i;

      baseEdgeOverhangs[i] = edgeOverrides.overhang;
      limitEdgeOverhangs[inverseI] = edgeOverrides.overhang + exteriorWallOffsetFromOverhangDatum;

      const roofSide = getRoofEdgeAlignedSide({
        boundingPerimeter,
        exactPerimeter,
        startEdge,
        edgeIndex: i,
      });

      updateBaseOverhangs(roofSide as RoofSide, edgeOverrides.overhang);
    }
  }

  // add the exterior wall offset to the base eave overhangs
  const edgeOverhangs = baseEdgeOverhangs.map((x) => x + exteriorWallOffsetFromOverhangDatum);

  // build the overhang data
  const overhangs = roofOverhangsNew({
    edgeOverhangs,
    baseEdgeOverhangs,
    limitEdgeOverhangs,
    limitEdgeNoOverhangs,
  });

  const rectangularOverhangs = rectangularOverhangsNew(
    startRoofOverhang,
    endRoofOverhang,
    leftEaveOverhang,
    rightEaveOverhang,
    baseStartRoofOverhang,
    baseEndRoofOverhang,
    baseLeftEaveOverhang,
    baseRightEaveOverhang,
  );

  // get wall offsets
  const wallLayerType = !legacyRoof ? WallLayerType.SUBSTRATE : WallLayerType.FINISH;

  const exteriorWallOffsetsWithFinish = getRoofPerimeterWallsOffsetsWithLayer({
    wallMassSystem: levelWallMasses,
    planarGraph: levelPlanarGraph,
    perimeter: exactPerimeter,
    wallSettings,
    wallLayerType: wallPlanes ? WallLayerType.FINISH : wallLayerType,
    wallPlanes,
  });

  const exteriorWallOffsetsFromOverhangDatum = getRoofPerimeterWallsOffsetsWithLayer({
    wallMassSystem: levelWallMasses,
    planarGraph: levelPlanarGraph,
    perimeter: exactPerimeter,
    wallSettings,
    wallLayerType:
      overhangDatum === WallLateralDatumAnchor.OUTSIDE_FACE_OF_SUBSTRATE
        ? WallLayerType.SUBSTRATE
        : WallLayerType.STRUCTURE,
    wallPlanes,
  });

  const rectangularExtWallOffsetWithFinish = getRoofPerimeterWallsOffsetsWithLayer({
    wallMassSystem: levelWallMasses,
    planarGraph: levelPlanarGraph,
    perimeter: boundingPerimeter,
    wallSettings,
    wallLayerType: wallPlanes ? WallLayerType.FINISH : wallLayerType,
    wallPlanes,
  });

  const rectangularExtWallOffsetsFromOverhangDatum = getRoofPerimeterWallsOffsetsWithLayer({
    wallMassSystem: levelWallMasses,
    planarGraph: levelPlanarGraph,
    perimeter: boundingPerimeter,
    wallSettings,
    wallLayerType:
      overhangDatum === WallLateralDatumAnchor.OUTSIDE_FACE_OF_SUBSTRATE
        ? WallLayerType.SUBSTRATE
        : WallLayerType.STRUCTURE,
    wallPlanes,
  });

  return {
    roofVolume: {
      roofSettings,
      legacyRoof,
      hasIndexedPeakDirection,
      heelHeightOffsets,
      baseHeight,
      ceilingHeight,
      overhangs,
      rectangularOverhangs,
      neighborRoofs,
      startTruncation,
      endTruncation,
      boundaryType,
      roofPlanSettings: { ...roofPlanSettings, ...subRoofPlanSettings },
      roofElevationPlanSettings: { ...roofElevationPlanSettings, ...subRoofElevationPlanSettings },
      startEdge,
      endEdge,
      legacyEaveReturns,
      legacyHeelHeightOffsets,
      exteriorWallOffsetsWithFinish,
      rectangularExtWallOffsetWithFinish,
      exteriorWallOffsetsFromOverhangDatum,
      rectangularExtWallOffsetsFromOverhangDatum,
    },
    index: index + 1,
  };
}

interface GetRoofEdgeAlignedSideOptions {
  readonly boundingPerimeter: PolyLine2;
  readonly exactPerimeter: PolyLine2;
  readonly startEdge: number;
  readonly edgeIndex: number;
}

function getRoofEdgeAlignedSide({
  boundingPerimeter,
  exactPerimeter,
  startEdge,
  edgeIndex,
}: GetRoofEdgeAlignedSideOptions): RoofSide {
  const roofSides = [RoofSide.START, RoofSide.END, RoofSide.LEFT, RoofSide.RIGHT];
  for (const side of roofSides) {
    if (isRoofEdgeAlignedToSide({ boundingPerimeter, exactPerimeter, startEdge, side, edgeIndex })) {
      return side;
    }
  }

  return RoofSide.INVALID;
}
